import { useQuery, useQueryClient } from "react-query";
import { defaultStaleTime } from "../withQueryData";
import { useRatePlansApi } from "./useRatePlansApi";

const queryKeys = {
    plans: (propertyId) => ['ratePlans', { propertyId }],
    restrictions: (planId) => ['restrictions', { planId: planId }],
}

export const usePlansData = (propertyId) => {
    const { findAllRatePlans, createRatePlan, updateRatePlan, deleteRatePlan, createRestriction, updateRestriction, deleteRestriction } = useRatePlansApi();

    const queryClient = useQueryClient();

    const cachedPlans = queryClient.getQueryData(queryKeys.plans(propertyId));

    const queryResults = useQuery({
        queryKey: queryKeys.plans(propertyId),
        queryFn: () => findAllRatePlans(propertyId),
        staleTime: defaultStaleTime,
        enabled: !!propertyId,
    });

    const _createPlan = async (plan) => {
        const createdPlan = await createRatePlan(propertyId, plan);

        const updatedPlans = cachedPlans && [...cachedPlans, createdPlan];
        queryClient.setQueryData(queryKeys.plans(propertyId), updatedPlans);
    };

    const _updatePlan = async (plan) => {
        const updatedPlan = await updateRatePlan(propertyId, plan);

        const updatedPlans = cachedPlans && cachedPlans.map(cachedPlan => cachedPlan.id === updatedPlan.id ? updatedPlan : cachedPlan);
        queryClient.setQueryData(queryKeys.plans(propertyId), updatedPlans);
    }

    const _deletePlan = async (planId) => {
        await deleteRatePlan(propertyId, planId);

        const updatedPlans = cachedPlans && cachedPlans.filter(cachedPlan => cachedPlan.id !== planId);
        queryClient.setQueryData(queryKeys.plans(propertyId), updatedPlans);
    }

    const _createRestriction = async (planId, restriction) => {
        const createdRestriction = await createRestriction(propertyId, planId, restriction);

        const updatedPlans = [...cachedPlans];
        const updatedPlan = updatedPlans.find(plan => plan.id === planId);

        updatedPlan.restrictions = [...updatedPlan.restrictions, createdRestriction];

        queryClient.setQueryData(queryKeys.plans(propertyId), updatedPlans);
    }

    const _updateRestriction = async (planId, restrictionId, restriction) => {
        const updatedRestriction = await updateRestriction(propertyId, planId, restrictionId, restriction);

        const updatedPlans = [...cachedPlans];
        const updatedPlan = updatedPlans.find(plan => plan.id === planId);

        const updatedRestrictions = updatedPlan.restrictions.map(restriction => {
            if (restriction.id === updatedRestriction.id) {
                return updatedRestriction;
            }
            else return restriction;
        });

        updatedPlan.restrictions = updatedRestrictions;

        queryClient.setQueryData(queryKeys.plans(propertyId), updatedPlans);
    }

    const _deleteRestriction = async (planId, restrictionId) => {
        await deleteRestriction(propertyId, planId, restrictionId);

        const cachedPlans = queryClient.getQueryData(queryKeys.plans(propertyId));
        const updatedPlans = [...cachedPlans];
        const updatedPlan = updatedPlans.find(plan => plan.id === planId);

        updatedPlan.restrictions = updatedPlan.restrictions.filter(restriction => restriction.id !== restrictionId);

        queryClient.setQueryData(queryKeys.plans(propertyId), updatedPlans);
    }

    return {
        queryResults,
        createPlan: _createPlan,
        updatePlan: _updatePlan,
        deletePlan: _deletePlan,
        createRestriction: _createRestriction,
        updateRestriction: _updateRestriction,
        deleteRestriction: _deleteRestriction,
    }
}
